export enum PriceSummaryJobStatus {
  PENDING = 'PENDING',
  INITIALISING = 'INITIALISING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  RETRYING = 'RETRYING',
  CANCELLED = 'CANCELLED',
}

export type PriceSummaryJob = {
  jobId: string;
  status: PriceSummaryJobStatus;
  createdAt: string;
  updatedAt: string;
  locationUrl: string;
  startDate: string;
  endDate: string;
  numberOfNights: number;
  currencyCode: string;
  countryCode: string;
  progress: number;
  days: number;
  submittedBy?: string;
  submittedByEmail?: string;
  cancelledBy?: string;
  cancelledByEmail?: string;
  retries: number;
};

export type PriceSummaryJobBody = {
  location_url: string;
  start_date: string;
  end_date: string;
  number_of_nights: number;
  currency_code: string;
  country_code: string;
};

export type PaginatedPriceParrotQueryParams = {
  page?: number;
  limit?: number;
  query?: string;
  status?: ScheduledPriceSummaryJobStatus;
  tag?: string;
};
export type PriceSummaryJobQueryParams = PaginatedPriceParrotQueryParams & {
  user_id?: string;
};

export type PaginatedPriceParrotResponse<T> = {
  data: Array<T>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};

export enum ScheduleInterval {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  ADHOC = 'ADHOC',
}

export enum ScheduledPriceSummaryJobStatus {
  PAUSED = 'PAUSED',
  ACTIVE = 'ACTIVE',
}

export type Property = {
  id: number;
  name?: string;
  location?: string;
  url: string;
};

export type ScheduledPriceSummaryJobVariation = {
  id: number;
  countryCode: string;
  currencyCode: string;
  numberOfNights: number;
  offsetDays: number;
  totalDays: number;
  createdAt: string;
};
export type ScheduledPriceSummaryJobVariationBody = Omit<ScheduledPriceSummaryJobVariation, 'id' | 'createdAt'>;

export type ScheduledPriceSummaryJob = {
  id: number;
  property: Property;
  status: ScheduledPriceSummaryJobStatus;
  scheduleInterval: ScheduleInterval;
  nextRunAt: string;
  createdBy?: string;
  createdByEmail?: string;
  updatedBy?: string;
  updatedByEmail?: string;
  createdAt: string;
  updatedAt: string;
  variations: Array<ScheduledPriceSummaryJobVariation>;
};

export type ScheduledPriceSummaryJobBody = {
  scheduleInterval: ScheduleInterval;
  locationUrl: string;
  scheduledJobVariations: Array<ScheduledPriceSummaryJobVariationBody>;
};

export type ScheduledPriceSummaryJobRun = {
  jobId: number;
  variation: ScheduledPriceSummaryJobVariation;
  status: PriceSummaryJobStatus;
  createdAt: string;
  updatedAt: string;
  retries: number;
  progress: number;
  days: number;
  errorMessage?: string;
};

// Control Centre types

/** Raw snake_case response from GET /api/vendor/parity/control-centre */
export type RawControlCentreProperty = {
  property_id: string | null;
  property_name: string | null;
  offer_bk: string;
  vendor_bk: string | null;
  vendor_name: string | null;
  impressions_overpriced_l30d: number;
  pct_impressions_overpriced_l30d: number | null;
  pct_overpriced_l30d: number | null;
  mpv_l7d: number | null;
  mpv_l30d: number | null;
  offer_country_code: string | null;
  offer_country_name: string | null;
  deal_tier: string | null;
  bedbank_lpc_ratio: number | null;
  yoy_ttv_change: number | null;
  vns_days: number | null;
  sf_opportunity_status: string | null;
  sf_last_activity: string | null;
  sf_closed_date: string | null;
  sf_created_date: string | null;
  property_parity_score_7d: string | null;
  property_parity_score_30d: string | null;
};

export type RawControlCentreResponse = {
  data: Array<RawControlCentreProperty>;
  total: number;
};

/** Frontend camelCase property used by the DataGrid */
export type ControlCentreProperty = {
  propertyId: string;
  propertyName: string;
  offerBk: string;
  vendorBk: string | null;
  offerCountryCode: string | null;
  offerCountryName: string | null;
  impressionsOverpriced: number;
  pctImpressionsOverpriced: number;
  pctOverpriced: number;
  mpv7d: number;
  mpv30d: number;
  connectivityActionStatus: string | null;
  lastActivityDate: string | null;
  closedDate: string | null;
  createdDate: string | null;
  vnsDays7d: number | null;
  openOpp: boolean;
  bedbankLpcPct: number | null;
  yoyDeclinePct: number | null;
  tier: number | null;
  propertyScore7d: string | null;
  propertyScore30d: string | null;
};

export type ControlCentreSummary = {
  propertiesFlagged: number;
  openOpportunities: number;
  casesCreatedToday: number;
  avgOverpricedImpressionsPct: number;
};

// Mutation types

export type CreateSfOpportunityBody = {
  propertyId: string;
};

export type CreateSfOpportunityResponse = {
  success: boolean;
  opportunity_id: string | null;
};

export type TriggerNotificationBody = {
  propertyId: string;
};

export type TriggerNotificationResponse = {
  success: boolean;
  opportunity_id: string | null;
  notification_triggered: boolean;
};

export type BulkCreateSfOpportunitiesBody = {
  items: Array<{ offer_bk: string; vendor_bk: string }>;
  reason?: string;
};

export type BulkCreateSfOpportunitiesResponse = {
  created: number;
  failed: number;
  errors?: Array<{ offer_bk: string; message: string }>;
};

/** Range-scoped summary fields from GET /api/vendor/parity/trend/offers/:offer_bk/summary */
export type ParityTrendSummary = {
  overpriced_searches: number;
  total_searches: number;
  /** Fraction e.g. 0.12 = 12% */
  avg_price_diff_when_overpriced: number | null;
};

export type ParityTrendDailyPoint = {
  report_date: string;
  region: string;
  /** Fraction 0–1 */
  pct_overpriced: number | null;
  overpriced_searches: number;
  total_searches: number;
};

export type ParityTrendCurrencyPoint = {
  currency: string;
  avg_price_diff_to_main: number | null;
};

/** GET /api/vendor/parity/trend/offers/:offer_bk/series */
export type ParityTrendSeriesResult = {
  daily_trend: Array<ParityTrendDailyPoint>;
};

export type ParityTrendSeriesApiResponse = {
  data: ParityTrendSeriesResult;
};

/** GET /api/vendor/parity/trend/offers/:offer_bk/summary */
export type ParityTrendRangeSummaryResult = {
  summary: ParityTrendSummary;
  by_currency: Array<ParityTrendCurrencyPoint>;
};

export type ParityTrendRangeSummaryApiResponse = {
  data: ParityTrendRangeSummaryResult;
};

// --- Chain rate parity dashboard (XTRNT-655) ----------------------------------
// Backed by GET /api/vendor/parity/trend/chains* in svc-vendor. Shapes mirror
// `ParityChain*` interfaces in svc-vendor/src/models/bigquery/types.ts —
// hand-copied because svc-vendor doesn't publish a contract package.

export const CHAIN_WEIGHTING_MODES = ['impressions', 'margin'] as const;
export type ChainWeightingMode = (typeof CHAIN_WEIGHTING_MODES)[number];

export type ParityChainListItem = {
  chain_name: string;
  property_count: number;
  total_jobs: number;
};

export type ParityChainSummary = {
  weighting: ChainWeightingMode;
  property_count: number;
  currency_count: number;
  total_jobs: number;
  jobs_in_parity: number;
  jobs_out_of_parity: number;
  jobs_severe: number;
  /** 0–1, under active weighting. */
  pct_out_of_parity: number | null;
  /** 0–1, always impression-weighted (severe is an absolute count). */
  pct_severe: number | null;
  /** Signed fraction, under active weighting. */
  avg_price_diff: number | null;
  lpc_rate_impressions: number;
  lpp_rate_impressions: number;
  margin_aud: number;
  revenue_aud: number;
  orders: number;
  /** Monthly revenue-at-risk (AUD); 50% haircut already applied server-side. */
  revenue_at_risk_aud: number | null;
  /**
   * Predicted booking lift if all overpriced jobs were brought to parity.
   * Fraction (0.098 = +9.8%). Null when chain has no in-parity bookings.
   * Negative values clamped to 0 server-side.
   */
  predicted_lift_pct: number | null;
  /**
   * Distinct continents this chain has properties in (geographic dbt `continent` column).
   * Populates the continent filter pills. Always returned by BE (empty array
   * when the chain has no rows in the date range).
   */
  continents: Array<string>;
  /**
   * Distinct major-competitor channels this chain is benchmarked against over the date range,
   * sourced from parity_chain_competitor_daily and ignoring the continent filter.
   * Curated bucket from the dbt export: e.g. 'Direct', 'Booking.com', 'Agoda.com', 'Expedia', 'Trip.com'.
   * Drives the competitor pill list and the rollup OTA-count caption.
   */
  major_competitors: Array<string>;
  /**
   * Range-aggregated avg premium when LE is overpriced, one entry per major-competitor.
   * Volume-weighted (mirrors `pct_out_of_parity`) and respects the continent filter — this is
   * a metric of the current view, not a stable per-chain fact like `major_competitors`.
   */
  avg_premium_by_competitor: Array<ParityChainCompetitorAggregate>;
  /**
   * Earliest report_date for which Price Parrot has any scraped data for this chain
   * (`YYYY-MM-DD`). Independent of the selected date range — represents the historical
   * floor — so the FE can disclaim ranges that start before any data exists. Null when
   * the chain has no parity rows at all.
   */
  data_available_from: string | null;
};

export type ParityChainDailyPoint = {
  report_date: string;
  total_jobs: number;
  jobs_in_parity: number;
  jobs_out_of_parity: number;
  pct_out_of_parity: number | null;
  avg_price_diff: number | null;
};

export type ParityChainTopProperty = {
  offer_bk: string;
  property_name: string | null;
  vendor_name: string | null;
  vendor_bk: string | null;
  continent: string | null;
  total_jobs: number;
  jobs_out_of_parity: number;
  pct_out_of_parity: number | null;
  avg_price_diff: number | null;
  /**
   * LPC share of bedbank impressions for the property over the requested range.
   * Same definition as `RawControlCentreProperty.bedbank_lpc_ratio`. 0–1 fraction.
   * Optional until svc-vendor adds it to the chain-properties response.
   */
  bedbank_lpc_ratio?: number | null;
  margin_aud: number;
  revenue_aud: number;
  /**
   * Property-grain monthly revenue-at-risk (AUD). Populated only when the 30d window is
   * selected on the BE; null for other windows. Display-only.
   */
  revenue_at_risk_aud: number | null;
};

export type ParityChainCompetitorPoint = {
  report_date: string;
  major_competitor: string;
  /** Avg LE premium over this competitor on rows where LE was overpriced (fraction). */
  avg_premium_when_le_overpriced: number | null;
  comparisons_le_overpriced: number;
  comparisons_total: number;
};

/** GET /api/vendor/parity/trend/chains */
export type ParityTrendChainsListResult = {
  chains: Array<ParityChainListItem>;
};
export type ParityTrendChainsListApiResponse = {
  data: ParityTrendChainsListResult;
};

/** GET /api/vendor/parity/trend/chains/:chain_name/series */
export type ParityTrendChainSeriesResult = {
  daily_trend: Array<ParityChainDailyPoint>;
};
export type ParityTrendChainSeriesApiResponse = {
  data: ParityTrendChainSeriesResult;
};

/** GET /api/vendor/parity/trend/chains/:chain_name/summary */
export type ParityTrendChainRangeSummaryResult = {
  summary: ParityChainSummary;
  by_currency: Array<ParityTrendCurrencyPoint>;
  top_properties: Array<ParityChainTopProperty>;
};
export type ParityTrendChainRangeSummaryApiResponse = {
  data: ParityTrendChainRangeSummaryResult;
};

/** GET /api/vendor/parity/trend/chains/:chain_name/properties */
export type ParityTrendChainPropertiesResult = {
  total_property_count: number;
  properties: Array<ParityChainTopProperty>;
};
export type ParityTrendChainPropertiesApiResponse = {
  data: ParityTrendChainPropertiesResult;
};

/**
 * Range-aggregated avg LE premium per major-competitor channel. Volume-weighted across days
 * (mirrors how `summary.pct_out_of_parity` is computed) and respects the continent filter.
 */
export type ParityChainCompetitorAggregate = {
  major_competitor: string;
  /** Volume-weighted avg LE premium across the range, fraction (e.g. 0.08 = +8%). */
  avg_premium_when_le_overpriced: number | null;
  /** Sum of overpriced comparisons across the range; used as the weight. */
  comparisons_le_overpriced: number;
  /** Sum of all comparisons across the range, for "X of Y" context in the UI. */
  comparisons_total: number;
};

/** GET /api/vendor/parity/trend/chains/:chain_name/competitors */
export type ParityTrendChainCompetitorsResult = {
  daily_trend: Array<ParityChainCompetitorPoint>;
};
export type ParityTrendChainCompetitorsApiResponse = {
  data: ParityTrendChainCompetitorsResult;
};
